import { Button, ButtonSize, ButtonVariant } from '@/components/shadcn/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command'
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { type Control, FieldPath, FieldValues } from 'react-hook-form'
import { TooltipButton } from '../ui/TooltipButton'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'

export interface SelectOption<T = string | number> {
  value: T
  label: string
  description?: string
  group?: string
}

interface SingleSelectComboboxProps<T = string | number> {
  options: readonly SelectOption<T>[]
  selectedValue?: T | null
  onSelectionChange: (selectedValue: T | null) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  allowClear?: boolean
  portalPopover?: boolean
  triggerVariant?: ButtonVariant
  triggerSize?: ButtonSize
  popoverContentClassName?: string
  renderOption?: (option: SelectOption<T>) => ReactNode
  renderSelectedOption?: (option: SelectOption<T>) => ReactNode
  footerContent?: (closePopover: () => void) => ReactNode
}

export function SingleSelectCombobox<T extends string | number>({
  triggerVariant = 'input',
  triggerSize,
  options,
  selectedValue,
  onSelectionChange,
  placeholder = 'Select an item...',
  searchPlaceholder = 'Search items...',
  emptyMessage = 'No items found.',
  className,
  disabled = false,
  allowClear = true,
  portalPopover = false,
  popoverContentClassName,
  renderOption,
  renderSelectedOption,
  footerContent,
}: SingleSelectComboboxProps<T>) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [triggerWidth, setTriggerWidth] = useState<number>(0)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Filter options based on search
  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
  const hasOptionGroups = filteredOptions.some((option) => !!option.group)
  const groupedFilteredOptions = useMemo(() => {
    const groups = new Map<string, SelectOption<T>[]>()
    for (const option of filteredOptions) {
      const groupName = option.group || 'Other'
      const current = groups.get(groupName) ?? []
      current.push(option)
      groups.set(groupName, current)
    }
    return Array.from(groups.entries())
  }, [filteredOptions])

  const renderOptionItem = (option: SelectOption<T>) => {
    const isSelected = selectedValue === option.value
    return (
      <CommandItem
        key={`${option.group ?? ''}-${String(option.value)}`}
        value={option.label}
        onSelect={() => handleSelect(option.value)}
        className="group max-w-full"
      >
        {renderOption ? (
          renderOption(option)
        ) : (
          <div className="flex max-w-full flex-col">
            <TooltipButton tooltip={option.label} side="left">
              <span className="truncate font-medium">{option.label}</span>
            </TooltipButton>
            {option.description && (
              <span className="text-xs text-muted-foreground group-data-[selected=true]:text-accent-foreground/80">
                {option.description}
              </span>
            )}
          </div>
        )}
        <IconCheckMarkOutline className={cn('ml-auto', isSelected ? 'opacity-100' : 'opacity-0')} />
      </CommandItem>
    )
  }

  // Get selected option for display
  const selectedOption = options.find((option) => option.value === selectedValue)

  const handleSelect = (value: T) => {
    if (selectedValue === value && allowClear) {
      // If the same value is selected and clear is allowed, clear the selection
      onSelectionChange(null)
    } else {
      // Set the new selection
      onSelectionChange(value)
    }
    setOpen(false)
  }

  // Update trigger width when component mounts or when the popover opens
  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth)
    }
  }, [open])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant={triggerVariant}
          size={triggerSize}
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
        >
          {selectedOption ? (
            (renderSelectedOption?.(selectedOption) ?? selectedOption.label)
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <IconDownChevronOutline className="size-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        portal={portalPopover}
        className={cn('p-0', popoverContentClassName)}
        style={{ width: triggerWidth ? `${triggerWidth}px` : 'auto' }}
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} value={searchValue} onValueChange={setSearchValue} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {hasOptionGroups ? (
              groupedFilteredOptions.map(([groupName, groupOptions]) => (
                <CommandGroup key={groupName} heading={groupName}>
                  {groupOptions.map((option) => renderOptionItem(option))}
                </CommandGroup>
              ))
            ) : (
              <CommandGroup>{filteredOptions.map((option) => renderOptionItem(option))}</CommandGroup>
            )}
          </CommandList>
          {footerContent && (
            <div onMouseDown={(event) => event.preventDefault()}>{footerContent(() => setOpen(false))}</div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Form field wrapper component for easier integration with React Hook Form
interface SingleSelectComboboxFieldProps<
  T = string | number,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  options: readonly SelectOption<T>[]
  description?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  allowClear?: boolean
  hideLabel?: boolean
  inlineLabel?: boolean
  triggerVariant?: ButtonVariant
  renderOption?: (option: SelectOption<T>) => ReactNode
  renderSelectedOption?: (option: SelectOption<T>) => ReactNode
}

export function SingleSelectComboboxField<
  T extends string | number,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  className,
  disabled,
  allowClear,
  triggerVariant = 'input',
  hideLabel = false,
  inlineLabel = false,
  renderOption,
  renderSelectedOption,
}: SingleSelectComboboxFieldProps<T, TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className, inlineLabel ? 'flex items-center gap-1' : '')}>
          <FormLabel hidden={hideLabel} className="whitespace-nowrap">
            {label}
          </FormLabel>
          <FormControl>
            <SingleSelectCombobox
              options={options}
              selectedValue={field.value || null}
              onSelectionChange={field.onChange}
              placeholder={placeholder}
              searchPlaceholder={searchPlaceholder}
              emptyMessage={emptyMessage}
              disabled={disabled}
              allowClear={allowClear}
              className={hideLabel || inlineLabel ? '!my-0' : ''}
              triggerVariant={triggerVariant}
              renderOption={renderOption}
              renderSelectedOption={renderSelectedOption}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
